import React, { useEffect, useState } from "react";
import Input from "../../form/Input";
import TimePicker from "../../form/timePicker/TimePicker";
import DatePicker from "../../form/DatePicker";
import Accordian from "../Accordian";
import AdvancedSettingsAddon from "../accordianContent/AdvancedSettingsAddon";
import Form from "../../form/Form";
import useForm from "../../../hooks/useForm";
import Button from "../../form/Button";
import { useMutation } from "@tanstack/react-query";
import { ticketAddEdit } from "../../../services/ticketService";
import { useOrganization } from "../../../context/OrganizationContext";
import { addTimeToDate, parseTimeString } from "../../../helpers";
import dayjs from "dayjs";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import Image from "../Image";
import Checkbox from "../../form/Checkbox";
import VarientForm from "../accordianContent/VarientForm";
import { t } from "i18next";
import LangSwitch from "../LangSwitch";

const validation = {
  name: { required: true, message: "Please enter addon name" },
  quantity: { required: true, message: "quantity is required" },
  salesStartDate: { required: true, message: "start date is required" },
  salesStartTime: { required: true, message: "start time is required" },
  salesEndDate: { required: true, message: "end date is required" },
  salesEndTime: { required: true, message: "end time is required" },
};

const ManageAddons = ({ eventDetails = {}, addonDetailsData = {} }) => {
  const navigate = useNavigate();
  const { selectedOrganization } = useOrganization();
  const { lang } = useSelector((state) => state?.langProvider);
  const [selectedLang, setSelectedLang] = useState(lang || "en");

  const {
    values,
    handleChange,
    handleSubmit,
    errors,
    setFieldsValue,
    formValidate,
  } = useForm({
    validationRules: validation,
    // initialState: { isVarient: true },
  });

  const faqData = [
    {
      title: t("Advanced settings"),
      content: (
        <AdvancedSettingsAddon
          values={values}
          handleFormChange={handleChange}
        />
      ),
    },
  ];

  const {
    mutate: addPaidAddon,
    isPending,
    error,
  } = useMutation({
    mutationFn: async (value) => {
      return await ticketAddEdit({
        ...value,
        name: { ...addonDetailsData?.name, [selectedLang]: value?.name },
        salesStartDate: value?.salesStartDate
          ? addTimeToDate(
              value?.salesStartDate,
              parseTimeString(value?.salesStartTime)
            )
          : null,
        salesEndDate: value?.salesEndDate
          ? addTimeToDate(
              value?.salesEndDate,
              parseTimeString(value?.salesEndTime)
            )
          : null,
        excerpt: {
          ...addonDetailsData?.excerpt,
          [selectedLang]: value?.excerpt,
        },
        pickupAtEvent: values?.pickupAtEvent ? "yes" : "no",
        eTicket: values?.eTicket ? "yes" : "no",
        showTicketOnSale: values?.showTicketOnSale ? "yes" : "no",
        event: eventDetails?._id,
        organization: selectedOrganization,
        type: "addons-event",
        paymentType: "paid",
        ...(addonDetailsData?._id && { id: addonDetailsData?._id }),
      });
    },
    onSuccess: (res) => {
      if (res?.status === 200 || res?.status === 201) {
        navigate(`/${lang}/user/events/tickets/${eventDetails?.uid}`);
      }
    },
  });

  useEffect(() => {
    if (addonDetailsData?._id) {
      setFieldsValue({
        name: addonDetailsData?.name?.[selectedLang],
        quantity: addonDetailsData?.quantity,
        image: addonDetailsData?.image,
        price: addonDetailsData?.price || 0,
        salesStartDate: addonDetailsData?.salesStartDate
          ? addonDetailsData?.salesStartDate
          : "",
        salesEndDate: addonDetailsData?.salesEndDate
          ? addonDetailsData?.salesEndDate
          : "",
        salesStartTime: addonDetailsData?.salesStartDate
          ? dayjs(addonDetailsData?.salesStartDate).format("hh:mm:ss A")
          : "",
        salesEndTime: addonDetailsData?.salesEndDate
          ? dayjs(addonDetailsData?.salesEndDate).format("hh:mm:ss A")
          : "",
        excerpt: addonDetailsData?.excerpt?.[selectedLang] || "",
        visibility: addonDetailsData?.visibility,
        quantityMin: addonDetailsData?.quantityMin,
        quantityMax: addonDetailsData?.quantityMax,
        salesChannel: addonDetailsData?.salesChannel,
        showTicketOnSale: addonDetailsData?.showTicketOnSale === "yes",
        eTicket: addonDetailsData?.eTicket === "yes",
        pickupAtEvent: addonDetailsData?.pickupAtEvent === "yes",
      });
    }
  }, [addonDetailsData?._id, selectedLang]);

  return (
    <Form
      className="space-y-5"
      handleSubmit={handleSubmit}
      onSubmit={addPaidAddon}
    >
      <LangSwitch
        langs={["en", "es"]}
        selectedLang={selectedLang}
        setSelectedLang={setSelectedLang}
      />
      {/* <Checkbox
        onChange={handleChange}
        checkboxName={"isVarient"}
        checked={values?.isVarient}
        checkboxLabel={
          <span className="font-medium">
            This add-on has multiple prices or varieties
          </span>
        }
      /> */}
      <div className="grid grid-cols-12 gap-5">
        <div className="col-span-9 space-y-5">
          <div className="relative">
            <Input
              labelClasses={"!text-lg !text-deep-purple !mb-2"}
              inputClasses={
                "!border-surface-05 !bg-surface-04 !h-12 !font-medium !text-deep-purple"
              }
              label={t("Addon name")}
              onChange={handleChange}
              value={values?.name}
              inputName={"name"}
              {...formValidate(errors, "name")}
            />
          </div>
          {/* {!values?.isVarient && ( */}
          <div className="flex gap-5 w-full">
            <div className="relative w-full">
              <Input
                label={t("Available quantity")}
                labelClasses={"!text-lg !text-deep-purple !mb-2"}
                inputClasses={
                  "!border-surface-05 !bg-surface-04 !h-12 !font-medium !text-deep-purple"
                }
                inputName={"quantity"}
                inputType="number"
                onKeyDown={(e) => {
                  const blockedKeys = ["+", "-", "e", "E", " ", "."];
                  if (
                    blockedKeys.includes(e.key) ||
                    (e.key === "=" && e.shiftKey)
                  ) {
                    e.preventDefault();
                    return;
                  }
                }}
                onChange={(e) => {
                  const value = e.target.value;
                  if (/^\d*$/.test(value)) {
                    e.target.value = value;
                    handleChange(e);
                  }
                }}
                value={values?.quantity}
                {...formValidate(errors, "quantity")}
              />
            </div>
            <div className="relative w-full">
              <Input
                label={
                  <div className="flex items-center gap-2">{t("Price")} </div>
                }
                labelClasses={"!text-lg !text-deep-purple !mb-2"}
                inputClasses={
                  "!border-surface-05 !bg-surface-04 !h-12 !font-medium !text-deep-purple !pl-10"
                }
                inputPrefix={eventDetails?.currency?.symbol || "$"}
                inputName={"price"}
                inputType="number"
                onKeyDown={(e) => {
                  const blockedKeys = ["+", "-", "e", "E", " ", "."];
                  if (
                    blockedKeys.includes(e.key) ||
                    (e.key === "=" && e.shiftKey)
                  ) {
                    e.preventDefault();
                    return;
                  }
                }}
                onChange={(e) => {
                  const value = e.target.value;
                  if (/^\d*$/.test(value)) {
                    e.target.value = value;
                    handleChange(e);
                  }
                }}
                value={values?.price}
                note={"Please enter a valid price greater than 50¢ (USD)."}
              />
            </div>
          </div>
          {/* )} */}
        </div>
        <div className="col-span-3">
          <div className="font-medium font-CanelaDeck !text-lg !text-deep-purple !mb-2">
            {t("Upload addons image")}
          </div>
          <label className="relative flex w-full aspect-[10/7] rounded-lg overflow-hidden border border-gray-200">
            <Image
              src={
                values?.image
                  ? values?.image instanceof File
                    ? URL.createObjectURL(values?.image)
                    : values?.image?.url ||
                      require("../../../../src/assets/images/image.png")
                  : require("../../../../src/assets/images/image.png")
              }
              alt="Uploaded Image"
              classes="object-cover"
            />
            <input
              type="file"
              className="h-0 w-0 opacity-0 absolute top-0 left-0"
              onChange={(e) => {
                const file = e.target.files[0];
                const type = file?.type?.split("/")[0];
                if (type === "image") {
                  handleChange({
                    target: { name: "image", value: file },
                  });
                }
                e.target.value = "";
              }}
            />
          </label>
        </div>
      </div>
      {/* {!values?.isVarient && ( */}
      <>
        <div className="grid grid-cols-2 gap-5">
          <div className="relative">
            <DatePicker
              label={t("Sales start")}
              labelClasses={"!text-lg !text-deep-purple !mb-2"}
              placeholder={t("Select Date")}
              inputClassName={
                "!border-surface-05 !bg-surface-04 !h-12 !font-medium !text-deep-purple"
              }
              onChange={(val) => {
                handleChange({
                  target: { name: "salesStartDate", value: val?.startDate },
                });
              }}
              value={{
                startDate: values?.salesStartDate,
                endDate: values?.salesStartDate,
              }}
              {...formValidate(errors, "salesStartDate")}
            />
          </div>
          <div className="relative">
            <TimePicker
              label={t("Start Time")}
              labelClasses={"!text-lg !text-deep-purple !mb-2"}
              inputClassName={
                "!border-surface-05 !bg-surface-04 !h-12 !font-medium !text-deep-purple"
              }
              onChange={(val) => {
                handleChange({
                  target: { name: "salesStartTime", value: val },
                });
              }}
              value={values?.salesStartTime}
              {...formValidate(errors, "salesStartTime")}
            />
          </div>
        </div>
        <div className="grid grid-cols-2 gap-5">
          <div className="relative">
            <DatePicker
              label={t("Sales end")}
              labelClasses={"!text-lg !text-deep-purple !mb-2"}
              placeholder={t("Select Date")}
              inputClassName={
                "!border-surface-05 !bg-surface-04 !h-12 !font-medium !text-deep-purple"
              }
              onChange={(val) => {
                handleChange({
                  target: { name: "salesEndDate", value: val?.startDate },
                });
              }}
              value={{
                startDate: values?.salesEndDate,
                endDate: values?.salesEndDate,
              }}
              {...formValidate(errors, "salesEndDate")}
            />
          </div>
          <div className="relative">
            <TimePicker
              label={t("End Time")}
              labelClasses={"!text-lg !text-deep-purple !mb-2"}
              inputClassName={
                "!border-surface-05 !bg-surface-04 !h-12 !font-medium !text-deep-purple"
              }
              onChange={(val) => {
                handleChange({
                  target: { name: "salesEndTime", value: val },
                });
              }}
              value={values?.salesEndTime}
              {...formValidate(errors, "salesEndTime")}
            />
          </div>
        </div>
        <div className="relative">
          <Accordian
            data={faqData}
            className="bg-gray-50 "
            buttonClass="text-deep-purple text-lg h-12"
            contentClass="text-gray-700"
          />
        </div>
        <div className="flex mt-7 gap-3 justify-end bg-magnolia py-4 sticky bottom-0 right-0 z-[2] pr-6">
          <Button
            variant="secondary"
            buttonLabel={t("Cancel")}
            buttonLabelClasses={"text-lg"}
            buttonClasses={"px-8"}
            buttonFunction={() => {
              navigate(`/${lang}/user/events/tickets/${eventDetails?.uid}`);
            }}
          />
          <Button
            buttonType="submit"
            buttonLabel={t(addonDetailsData?._id ? "Update" : "Save")}
            buttonLabelClasses={"text-lg"}
            buttonClasses={"px-8"}
            {...(isPending && {
              loading: true,
              buttonIconPosition: "left",
              isDisable: true,
            })}
          />
        </div>
      </>
      {values?.isVarient && <VarientForm />}
    </Form>
  );
};

export default ManageAddons;
